/** The permissions a token or an AI tool connection may carry, as checkboxes: the Create token dialog and the
 *  consent page offer the same choice in the same words, grouped Read, GFT only and Change. */

import { useSession } from '@/session/SessionContext';

import { GROUP_LABELS, SCOPES, type ScopeGroup } from './mcpScopes';

export type Scope = (typeof SCOPES)[number];

const GROUPS: ScopeGroup[] = ['read', 'internal', 'change'];
export const WRITES_OFF = 'Changes through AI tools are switched off here, so these do nothing for now.';

function Group({
  group,
  scopes,
  chosen,
  off,
  onToggle,
}: {
  group: ScopeGroup;
  scopes: Scope[];
  chosen: string[];
  off: boolean;
  onToggle: (key: string, on: boolean) => void;
}) {
  return (
    <div role="group" aria-label={GROUP_LABELS[group]}>
      <span className="hint">{GROUP_LABELS[group]}</span>
      {off && <p className="hint">{WRITES_OFF}</p>}
      <ul className="check-list">
        {scopes.map(({ key, label, hint }) => (
          <li key={key}>
            <label className="check">
              <input
                type="checkbox"
                checked={!off && chosen.includes(key)}
                disabled={off}
                onChange={(e) => onToggle(key, e.target.checked)}
              />
              <span className="ck-body">
                {label} <span className="hint">({hint})</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ScopeChecks({ held, chosen, onToggle }: { held: Scope[]; chosen: string[]; onToggle: (key: string, on: boolean) => void }) {
  const { writesEnabled } = useSession();
  return (
    <fieldset className="field-group">
      <legend className="label">What it may do</legend>
      {GROUPS.map((group) => {
        const scopes = held.filter((scope) => scope.group === group);
        const off = group === 'change' && !writesEnabled;
        return scopes.length > 0 && <Group key={group} group={group} scopes={scopes} chosen={chosen} off={off} onToggle={onToggle} />;
      })}
      {held.length === 0 ? (
        <span className="field-error" role="alert">
          Your role gives none of the access a token can carry.
        </span>
      ) : (
        chosen.length === 0 && (
          <span className="field-error" role="alert">
            Choose at least one.
          </span>
        )
      )}
    </fieldset>
  );
}
